import { useState, useCallback, useEffect } from 'react';
import { useConnection } from 'wagmi';
import { useDirectListings } from '@/hooks/marketplace/useDirectListings';
import { parseUnits, type Address } from 'viem';
import { getNativeCurrency, getTokensByChainId } from '@/constants/marketplace/tokens';
import toast from '@/util/toast';
import { getMarketplaceContract } from '@/constants/marketplace/contracts';
import { chainSettings, defaultNativeAddress, ChainType } from '@/constants/chain-specific/chains';
import ApprovalTransactionFlow, { AssetType } from '@/components/shared/ApprovalTransactionFlow';
import { useApprovalFlow } from '@/hooks/shared/useApprovalFlow';
import { getCollectionByAddress } from '@/constants/marketplace/collections';
import { marketplaceSettings } from '@/constants/marketplace/marketSettings';
import { Button, Input, Select, SelectItem, Checkbox, Card, CardBody, CardHeader, Divider, Image, Chip } from '@heroui/react';
interface CreateListingFormProps {
  chainId: number;
  assetContract: Address;
  tokenId: string;
  onSuccess?: () => void;
}
export default function CreateListingForm({
  chainId,
  assetContract,
  tokenId,
  onSuccess
}: CreateListingFormProps) {
  // State for form values and validation
  // Get supported tokens for this chain including native currency
  const erc20Tokens = getTokensByChainId(chainId)?.tokens || [];
  const nativeToken = getNativeCurrency(chainId);

  // Combine native currency with ERC20 tokens
  const supportedTokens = nativeToken ? [nativeToken, ...erc20Tokens] : erc20Tokens;
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [currency, setCurrency] = useState<Address | undefined>(nativeToken?.address as Address);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)); // 30 days from now
  const [isReserved, setIsReserved] = useState(false);
  const [reservedAddress, setReservedAddress] = useState('');

  // Get user account and marketplace functions
  const {
    address
  } = useConnection();
  const {
    createListing,
    isCreatingListing
  } = useDirectListings(chainId);

  // Get marketplace contract address for approval
  const marketplace = getMarketplaceContract(chainId);
  const marketplaceAddress = marketplace?.address as Address;

  // Get collection to check if it's ERC1155
  const collection = getCollectionByAddress(assetContract, chainId as ChainType);
  const isERC1155 = collection?.type === 'ERC1155';

  // Use the approval flow hook
  const {
    isApprovalDialogOpen,
    setIsApprovalDialogOpen,
    checkNeedsApproval,
    executeApproval
  } = useApprovalFlow(chainId);

  // Function to create NFT listing (called after approval if needed)
  const executeListingCreation = useCallback(async () => {
    try {
      if (!address || !assetContract || !tokenId || !price || !currency || !startDate || !endDate) {
        throw new Error('Missing required fields');
      }
      if (endDate <= startDate) {
        throw new Error('End date must be after start date');
      }
      if (isReserved && !reservedAddress) {
        throw new Error('Reserved address is required for reserved listings');
      }

      // Find the token for decimal calculation
      const selectedToken = supportedTokens.find(token => token.address === currency);
      if (!selectedToken) {
        throw new Error('Selected currency not found');
      }
      if (!tokenId) {
        throw new Error('Token ID is required');
      }

      // Create listing
      await createListing(assetContract, BigInt(tokenId), BigInt(quantity), currency, price,
      // Pass as string which will be converted to BigInt by the hook
      Math.floor(startDate.getTime() / 1000), Math.floor(endDate.getTime() / 1000), isReserved && !!reservedAddress);

      // If successful, call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
      return {
        success: true,
        hash: undefined
      };
    } catch (err) {
      console.error('Error creating listing:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to create listing: ${errorMessage}`);
      return {
        success: false,
        hash: undefined
      };
    }
  }, [address, assetContract, tokenId, price, quantity, currency, startDate, endDate, isReserved, reservedAddress, createListing, supportedTokens, onSuccess]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address) {
      toast.error('Please connect your wallet');
      return;
    }
    if (!assetContract || !tokenId || !price || !currency || !startDate || !endDate) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (endDate <= startDate) {
      toast.error('End date must be after start date');
      return;
    }
    if (!marketplaceAddress) {
      toast.error('Marketplace contract not found for this chain');
      return;
    }

    // Open the approval transaction flow
    setIsApprovalDialogOpen(true);
  };

  // Configure approval text for better user experience
  const approvalConfig = {
    assetType: isERC1155 ? AssetType.ERC1155 : AssetType.ERC721,
    assetAddress: assetContract,
    spenderAddress: marketplaceAddress,
    chainId: chainId,
    tokenId: tokenId ? BigInt(tokenId) : undefined,
    text: {
      modalTitle: {
        approval: `Approve ${isERC1155 ? 'ERC-1155' : 'NFT'} for Marketplace`,
        main: 'Creating Listing',
        complete: 'Listing Created'
      },
      steps: {
        approval: 'Approve',
        main: 'List'
      },
      messages: {
        checking: 'Checking if approval is needed...',
        needsApproval: `You need to approve this ${isERC1155 ? 'ERC-1155 token' : 'NFT'} before listing`,
        approving: 'Please confirm the approval transaction in your wallet',
        approved: `${isERC1155 ? 'ERC-1155 token' : 'NFT'} approved! Ready to create listing`,
        mainAction: `Creating ${isERC1155 ? 'ERC-1155' : 'NFT'} listing...`,
        complete: `Your ${isERC1155 ? 'ERC-1155 token' : 'NFT'} has been listed successfully!`
      },
      buttons: {
        approve: `Approve ${isERC1155 ? 'ERC-1155' : 'NFT'}`,
        executeMain: 'Create Listing',
        close: 'Close'
      }
    }
  };

  // Calculate fees and amount received
  const calculateFees = useCallback(() => {
    if (!price || isNaN(Number(price))) {
      return {
        platformFee: 0,
        royalty: 0,
        amountReceived: 0,
        platformFeePercentage: 0,
        royaltyPercentage: 0
      };
    }
    const priceValue = Number(price);
    const platformFeePercentage = marketplaceSettings[chainId]?.platformFee || 0; // in BIPS
    const royaltyPercentage = collection?.royalty?.percentTotal || 0; // in BIPS

    const platformFeeAmount = priceValue * (platformFeePercentage / 10000);
    const royaltyAmount = priceValue * (royaltyPercentage / 10000);
    const amountReceived = priceValue - platformFeeAmount - royaltyAmount;
    return {
      platformFee: platformFeeAmount,
      royalty: royaltyAmount,
      amountReceived: Math.max(0, amountReceived),
      platformFeePercentage: platformFeePercentage / 100,
      // Convert to percentage for display
      royaltyPercentage: royaltyPercentage / 100 // Convert to percentage for display
    };
  }, [price, chainId, collection]);
  const feeCalculation = calculateFees();

  // Debug logging
  useEffect(() => {
    console.log('CreateListingForm - Approval config:', {
      isERC1155,
      assetContract,
      marketplaceAddress,
      chainId,
      tokenId,
      collection: collection?.name
    });
  }, [isERC1155, assetContract, marketplaceAddress, chainId, tokenId, collection]);

  // Date handling
  const handleStartDateChange = (date: Date) => {
    setStartDate(date);
    // If end date is before or equal to start date, set end date to start date + 1 day
    if (endDate <= date) {
      const newEndDate = new Date(date);
      newEndDate.setDate(date.getDate() + 1);
      setEndDate(newEndDate);
    }
  };
  return <div className="w-full max-w-2xl mx-auto">
      {/* Approval Transaction Flow Modal */}
      <ApprovalTransactionFlow config={approvalConfig} isOpen={isApprovalDialogOpen} onClose={() => setIsApprovalDialogOpen(false)} checkNeedsApproval={checkNeedsApproval} executeApproval={executeApproval} executeMainTransaction={executeListingCreation} onApprovalSuccess={() => {
      toast.success('NFT approved successfully!');
    }} onMainTransactionSuccess={() => {
      toast.success('NFT listed successfully!');
      if (onSuccess) onSuccess();
    }} />


      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Price input */}
        <Input label="Price" placeholder="Enter listing price" labelPlacement='outside' value={price} onValueChange={setPrice} variant="bordered" isRequired description="Set your asking price for this NFT" startContent={<div className="pointer-events-none flex items-center">
              <span className="text-default-400 text-small">💰</span>
            </div>} />

        {/* Fee calculation display */}
        {price && !isNaN(Number(price)) && Number(price) > 0 && <Card className="bg-default-50 border-default-200">
            <CardBody className="py-3 px-4">
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-default-600">Listing Price:</span>
                  <span className="font-medium">{price} {supportedTokens.find(t => t.address === currency)?.symbol || ''}</span>
                </div>

                {feeCalculation.platformFeePercentage > 0 && <div className="flex justify-between items-center">
                    <span className="text-default-600">Platform Fee ({feeCalculation.platformFeePercentage}%):</span>
                    <span className="text-red-500">-{feeCalculation.platformFee.toFixed(6)} {supportedTokens.find(t_0 => t_0.address === currency)?.symbol || ''}</span>
                  </div>}

                {feeCalculation.royaltyPercentage > 0 && (collection?.royalty?.breakdown ? collection.royalty.breakdown.map((royalty, index) => {
              const royaltyAmount_0 = Number(price) * (royalty.percent / 10000);
              return <div key={index} className="flex justify-between items-center">
                          <span className="text-default-600">{royalty.description} ({(royalty.percent / 100).toFixed(2)}%):</span>
                          <span className="text-red-500">-{royaltyAmount_0.toFixed(6)} {supportedTokens.find(t_1 => t_1.address === currency)?.symbol || ''}</span>
                        </div>;
            }) : <div className="flex justify-between items-center">
                      <span className="text-default-600">Creator Royalty ({feeCalculation.royaltyPercentage}%):</span>
                      <span className="text-red-500">-{feeCalculation.royalty.toFixed(6)} {supportedTokens.find(t_2 => t_2.address === currency)?.symbol || ''}</span>
                    </div>)}

                <Divider className="my-1" />

                <div className="flex justify-between items-center">
                  <span className="font-medium text-default-700">You'll receive:</span>
                  <span className="font-bold text-green-600 text-base">{feeCalculation.amountReceived.toFixed(6)} {supportedTokens.find(t_3 => t_3.address === currency)?.symbol || ''}</span>
                </div>
              </div>
            </CardBody>
          </Card>}

        {/* Currency selection */}
        <Select label="Currency" labelPlacement="outside" placeholder="Select payment currency" selectedKeys={currency ? [currency] : []} onSelectionChange={keys => {
        const selectedKey = Array.from(keys)[0] as string;
        setCurrency(selectedKey as Address);
      }} variant="bordered" isRequired description="Choose which token to accept as payment" renderValue={items => {
        return items.map(item => {
          const token_0 = supportedTokens.find(t_4 => t_4.address === item.key);
          return <div key={item.key} className="flex items-center gap-2">
                  {token_0?.logo && <Image alt={token_0.symbol} className="flex-shrink-0" height={20} src={token_0.logo} width={20} />}
                  <div className="flex flex-col">
                    <span>{token_0?.symbol}</span>
                  </div>
                </div>;
        });
      }}>
          {supportedTokens.map(token_1 => <SelectItem key={token_1.address} textValue={`${token_1.symbol} - ${token_1.name}`}>
              <div className="flex items-center gap-2">
                {token_1.logo && <Image alt={token_1.symbol} className="flex-shrink-0" height={24} src={token_1.logo} width={24} />}
                <div className="inline-block">
                  <span className="text-small pr-2">{token_1.symbol}</span>
                  <span className="text-tiny text-default-400">{token_1.name}</span>
                </div>
              </div>
            </SelectItem>)}
        </Select>

        {/* Quantity */}

        {collection?.type === 'ERC1155' && <Input label="Quantity" placeholder="Enter quantity to list" type="number" value={quantity} onValueChange={setQuantity} variant="bordered" min="1" isRequired description={isERC1155 ? "Number of tokens to list" : "Should be 1 for unique NFTs"} startContent={<div className="pointer-events-none flex items-center">
                <span className="text-default-400 text-small">#</span>
              </div>} />}

        {/* Date inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Start Date" type="datetime-local" value={startDate.toISOString().slice(0, 16)} onValueChange={value => handleStartDateChange(new Date(value))} variant="bordered" min={new Date().toISOString().split('T')[0]} isRequired description="When listing becomes active" />

          <Input label="End Date" type="datetime-local" value={endDate.toISOString().slice(0, 16)} onValueChange={value_0 => setEndDate(new Date(value_0))} variant="bordered" min={startDate ? new Date(startDate.getTime() + 86400000).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]} isRequired description="When listing expires" />
        </div>

        {/* Reserved listing toggle */}
        <Checkbox isSelected={isReserved} onValueChange={setIsReserved} color="primary">
          <div className="flex flex-col">
            <span className="text-small">Reserved listing</span>
            <span className="text-tiny text-default-500">
              Only a specific address can purchase this NFT
            </span>
          </div>
        </Checkbox>

        {/* Conditional reserved address field */}
        {isReserved && <Input label="Reserved Address" placeholder="0x..." value={reservedAddress} onValueChange={setReservedAddress} variant="bordered" isRequired={isReserved} description="Only this address will be able to purchase the NFT" startContent={<div className="pointer-events-none flex items-center">
                <span className="text-default-400 text-small">🔒</span>
              </div>} />}

        <Divider />

        {/* Submit button */}
        <Button type="submit" color="primary" size="lg" isLoading={isCreatingListing} isDisabled={isCreatingListing} className="w-full">
          {isCreatingListing ? 'Creating Listing...' : 'Create Listing'}
        </Button>
      </form>


    </div>;
}